(() => {
  const coordinate = (value, limit) => {
    if (value == null || String(value).trim() === '') return null
    const number = Number(String(value).trim().replace(',', '.'))
    return Number.isFinite(number) && Math.abs(number) <= limit ? number : null
  }

  const validCoordinates = (latitude, longitude) =>
    latitude !== null && longitude !== null && !(latitude === 0 && longitude === 0)

  const normalize = (value) =>
    String(value || '')
      .normalize('NFD')
      .replace(/[\u0300-\u036f]/g, '')
      .toLowerCase()
      .trim()

  const distanceBetween = (origin, destination) => {
    const toRadians = (degrees) => (degrees * Math.PI) / 180
    const earthRadius = 6371
    const latitudeDelta = toRadians(destination.latitude - origin.latitude)
    const longitudeDelta = toRadians(destination.longitude - origin.longitude)
    const originLatitude = toRadians(origin.latitude)
    const destinationLatitude = toRadians(destination.latitude)
    const haversine =
      Math.sin(latitudeDelta / 2) ** 2 +
      Math.cos(originLatitude) *
        Math.cos(destinationLatitude) *
        Math.sin(longitudeDelta / 2) ** 2

    return earthRadius * 2 * Math.atan2(Math.sqrt(haversine), Math.sqrt(1 - haversine))
  }

  const streetAddress = (value) => String(value || '')
    .replace(/\s+\b(?:lj|loja|sala|conjunto|andar)\b.*$/i, '')
    .replace(/(\d+)\s+\d+\s*$/, '$1')
    .replace(/^av\.?\s+dr\.?\s+/i, 'Avenida Doutor ')
    .trim()

  const confirmedAddress = (result, data) => {
    const latitude = coordinate(result?.lat, 90)
    const longitude = coordinate(result?.lon, 180)
    const cityMatches = normalize(result?.city) === normalize(data.city)
    const stateMatches = normalize(result?.state_code) === normalize(data.state) ||
      normalize(result?.state) === normalize(data.state) ||
      (!result?.state_code && normalize(result?.county_code) === normalize(data.state))
    const postcode = String(data.postcode || '').replace(/\D/g, '')
    const postcodeMatches = postcode.length === 8 && postcode === String(result?.postcode || '').replace(/\D/g, '')
    const number = streetAddress(data.address).match(/\b(\d+[a-z]?)\s*$/i)?.[1]
    const numberMatches = !number || normalize(result?.housenumber) === normalize(number)
    return validCoordinates(latitude, longitude) && result?.country_code === 'br' &&
      ['building', 'amenity'].includes(result?.result_type) &&
      result?.rank?.confidence >= 0.95 && cityMatches && numberMatches &&
      (stateMatches || (!result?.state_code && postcodeMatches))
  }

  const initializeLocator = (root) => {
    if (root.dataset.initialized === 'true' || typeof window.L === 'undefined') return

    const mapElement = root.querySelector('[data-partner-map]')
    const searchInput = root.querySelector('[data-partner-search]')
    const countElement = root.querySelector('[data-partner-count]')
    const emptyElement = root.querySelector('[data-partner-empty]')
    const geolocateButton = root.querySelector('[data-partner-geolocate]')
    const status = root.querySelector('[data-location-status]')
    const apiKey = (root.dataset.geocodingKey || '').trim()
    let interacted = false
    let stopped = false
    const say = (message) => { if (status) status.textContent = message }
    const itemElements = Array.from(root.querySelectorAll('[data-partner]'))

    if (!mapElement) return

    root.dataset.initialized = 'true'

    const initialLatitude = Number(mapElement.dataset.initialLatitude) || -14.235004
    const initialLongitude = Number(mapElement.dataset.initialLongitude) || -51.92528
    const initialZoom = Number(mapElement.dataset.initialZoom) || 4
    const map = window.L.map(mapElement, { scrollWheelZoom: false }).setView(
      [initialLatitude, initialLongitude],
      initialZoom
    )

    window.L.tileLayer('https://tile.openstreetmap.org/{z}/{x}/{y}.png', {
      maxZoom: 19,
      attribution: '&copy; <a href="https://www.openstreetmap.org/copyright">OpenStreetMap</a> contributors',
    }).addTo(map)
    if (apiKey) map.attributionControl.addAttribution('<a href="https://www.geoapify.com/">Geoapify</a> | © <a href="https://openaddresses.io/">OpenAddresses</a> contributors')

    const markerIcon = window.L.divIcon({
      className: 'partners-locator__marker-shell',
      html: '<span class="partners-locator__marker"></span>',
      iconSize: [32, 32],
      iconAnchor: [16, 31],
      popupAnchor: [0, -30],
    })

    const partners = itemElements.map((element) => {
      const latitude = coordinate(element.dataset.latitude, 90)
      const longitude = coordinate(element.dataset.longitude, 180)
      const hasCoordinates = validCoordinates(latitude, longitude)
      const address = [element.dataset.address, element.dataset.city, element.dataset.state, element.dataset.postcode, 'Brasil'].filter(Boolean).join(', ')
      const externalLink = element.querySelector('[data-address-map]')
      if (externalLink && element.dataset.address) {
        externalLink.href = 'https://www.google.com/maps/search/?api=1&query=' + encodeURIComponent(address)
        externalLink.hidden = false
      }
      const button = element.querySelector('[data-focus-partner]')
      if (button) {
        button.hidden = false
        button.disabled = !hasCoordinates
        button.textContent = hasCoordinates ? 'Ver no mapa' : apiKey ? 'Localizando...' : 'Localização indisponível'
      }
      let marker = null

      if (hasCoordinates) {
        const popup = document.createElement('strong')
        popup.textContent = element.dataset.name || 'Parceiro Jaguar'
        marker = window.L.marker([latitude, longitude], { icon: markerIcon })
          .bindPopup(popup)
          .addTo(map)
      }

      return {
        element,
        latitude,
        longitude,
        hasCoordinates,
        marker,
        address,
        button,
        search: normalize(element.dataset.search),
      }
    })

    const visibleMarkers = () =>
      partners.filter((partner) => !partner.element.hidden && partner.marker).map((partner) => partner.marker)

    const frameMarkers = (markers) => {
      if (markers.length === 1) {
        map.setView(markers[0].getLatLng(), 13)
      } else if (markers.length > 1) {
        map.fitBounds(window.L.featureGroup(markers).getBounds().pad(0.16), { maxZoom: 13 })
      }
    }

    const activatePartner = (partner, moveFocus = true) => {
      interacted = true
      partners.forEach((item) => item.element.classList.remove('is-active'))
      partner.element.classList.add('is-active')

      if (partner.marker) {
        map.flyTo([partner.latitude, partner.longitude], 13, { duration: 0.6 })
        partner.marker.openPopup()
      }

      if (moveFocus) {
        partner.element.scrollIntoView({ behavior: 'smooth', block: 'nearest' })
      }
    }

    partners.forEach((partner) => {
      const focusButton = partner.element.querySelector('[data-focus-partner]')
      if (focusButton) focusButton.addEventListener('click', () => {
        if (partner.marker) activatePartner(partner, false)
        else say('Endereço ainda não localizado. Use Consultar endereço para abrir a busca no mapa.')
      })
      if (partner.marker) partner.marker.on('click', () => activatePartner(partner))
    })

    const applySearch = () => {
      const query = normalize(searchInput ? searchInput.value : '')
      let visibleCount = 0

      partners.forEach((partner) => {
        const postalQuery = query.replace(/\D/g, '')
        const matches = query === '' || partner.search.includes(query) ||
          (/^[\d\s-]+$/.test(query) && postalQuery.length > 0 &&
            (partner.element.dataset.postcode || '').replace(/\D/g, '').includes(postalQuery))
        partner.element.hidden = !matches
        if (partner.marker) {
          if (matches && !map.hasLayer(partner.marker)) partner.marker.addTo(map)
          if (!matches && map.hasLayer(partner.marker)) partner.marker.removeFrom(map)
        }
        if (matches) visibleCount += 1
      })

      if (countElement) countElement.textContent = String(visibleCount)
      if (emptyElement) emptyElement.hidden = visibleCount !== 0
      if (query !== '') frameMarkers(visibleMarkers())
    }

    if (searchInput) searchInput.addEventListener('input', () => { interacted = true; applySearch() })

    if (geolocateButton) {
      geolocateButton.addEventListener('click', () => {
        if (!navigator.geolocation) {
          geolocateButton.textContent = 'Localização indisponível'
          return
        }

        geolocateButton.disabled = true
        geolocateButton.textContent = 'Localizando...'

        navigator.geolocation.getCurrentPosition(
          ({ coords }) => {
            const partnersWithCoordinates = partners.filter((partner) => partner.hasCoordinates)
            const nearest = partnersWithCoordinates.sort(
              (first, second) =>
                distanceBetween(coords, first) - distanceBetween(coords, second)
            )[0]

            if (nearest) {
              if (searchInput) searchInput.value = ''
              applySearch()
              activatePartner(nearest)
              say(partners.some((partner) => !partner.hasCoordinates)
                ? 'Mais próximo entre os parceiros com localização disponível.' : '')
            } else say('Ainda não há parceiros com localização disponível no mapa. Busque por cidade ou CEP.')
            geolocateButton.disabled = false
            geolocateButton.textContent = 'Usar minha localização'
          },
          () => {
            geolocateButton.disabled = false
            geolocateButton.textContent = 'Não foi possível obter sua localização'
          },
          { enableHighAccuracy: false, timeout: 10000, maximumAge: 300000 }
        )
      })
    }

    applySearch()
    frameMarkers(visibleMarkers())

    // Resolve sequentially; the cache is per browser and changes with the address.
    const resolveAddresses = async () => {
      const missing = partners.filter((partner) => !partner.hasCoordinates)
      if (!missing.length) return
      if (!apiKey) {
        say('Consulte os parceiros na lista e abra o endereço para ver a localização.')
        return
      }
      if (geolocateButton) geolocateButton.disabled = true
      say('Localizando endereços dos parceiros...')
      for (const partner of missing) {
        if (!root.isConnected || stopped) break
        if (!partner.element.dataset.address || !partner.element.dataset.city) continue
        const cacheKey = 'jaguar-geocode-v2:' + normalize(partner.address)
        let result = null
        try {
          const cached = JSON.parse(localStorage.getItem(cacheKey))
          if (cached && cached.expires > Date.now() && confirmedAddress(cached.result, partner.element.dataset)) result = cached.result
        } catch (_) { /* Storage may be unavailable. */ }
        if (!result) {
          const data = partner.element.dataset
          const clean = streetAddress(data.address)
          const searches = [...new Set([
            [clean, data.city, data.state, data.postcode, 'Brasil'].filter(Boolean).join(', '),
            [clean, data.city, data.state, 'Brasil'].filter(Boolean).join(', '),
          ])]
          for (const text of searches) {
            if (stopped || !root.isConnected) break
            const controller = new AbortController()
            const timeout = window.setTimeout(() => controller.abort(), 10000)
            try {
              const params = new URLSearchParams({ text, filter: 'countrycode:br', lang: 'pt', limit: '3', format: 'json', apiKey })
              const response = await fetch('https://api.geoapify.com/v1/geocode/search?' + params, { signal: controller.signal, credentials: 'omit' })
              if ([401, 403, 429].includes(response.status)) stopped = true
              if (!response.ok) throw new Error('Geocoding unavailable')
              result = (await response.json()).results?.find((candidate) => confirmedAddress(candidate, data))
            } catch (_) { result = null }
            finally { window.clearTimeout(timeout) }
            await new Promise((resolve) => window.setTimeout(resolve, 350))
            if (result) break
          }
        }
        if (!root.isConnected) break
        const latitude = coordinate(result?.lat, 90)
        const longitude = coordinate(result?.lon, 180)
        // Never present a city/ZIP centroid or a low-confidence match as a store.
        if (!confirmedAddress(result, partner.element.dataset)) {
          if (partner.button) {
            partner.button.disabled = true
            partner.button.textContent = 'Endereço não confirmado'
          }
          continue
        }
        try { localStorage.setItem(cacheKey, JSON.stringify({ result, expires: Date.now() + 30 * 86400000 })) } catch (_) {}
        partner.latitude = latitude
        partner.longitude = longitude
        partner.hasCoordinates = true
        if (partner.button) {
          partner.button.disabled = false
          partner.button.textContent = 'Ver no mapa'
        }
        const popup = document.createElement('strong')
        popup.textContent = partner.element.dataset.name || 'Parceiro Jaguar'
        partner.marker = window.L.marker([latitude, longitude], { icon: markerIcon }).bindPopup(popup)
        if (!partner.element.hidden) partner.marker.addTo(map)
        partner.marker.on('click', () => activatePartner(partner))
        if (!interacted) frameMarkers(visibleMarkers())
      }
      if (geolocateButton) geolocateButton.disabled = false
      const unresolved = partners.filter((partner) => !partner.hasCoordinates)
      unresolved.forEach((partner) => {
        if (partner.button) {
          partner.button.disabled = true
          partner.button.textContent = stopped ? 'Localização indisponível' : 'Endereço não confirmado'
        }
      })
      say(unresolved.length ? 'Alguns endereços não puderam ser confirmados no mapa. Consulte o endereço pela lista.' : '')
    }
    resolveAddresses()
  }

  const initializeAll = () => {
    const roots = document.querySelectorAll('[data-partners-locator]')
    if (typeof window.L === 'undefined') {
      window.setTimeout(initializeAll, 120)
      return
    }
    roots.forEach(initializeLocator)
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', initializeAll)
  } else {
    initializeAll()
  }

  document.addEventListener('shopify:section:load', initializeAll)
})()
